import { useState } from 'react';
import type { PaymentMode } from '@dunea/shared';
import {
  apiConfirmPayment,
  apiGetPaymentProof,
  apiListPayments,
  apiRefundPayment,
  apiRejectPayment,
  type PaymentListRow,
} from '~/lib/api/payments';
import { classNames } from '~/utils/classNames';
import {
  apiErrorMessage,
  awaitingUpiCheck,
  canMarkPaid,
  canRefund,
  canReject,
  formatMinor,
  majorToMinor,
  minorToMajorInput,
  paymentDate,
  paymentStatusMeta,
  providerLabel,
  purposeLabel,
  refundableAmount,
} from './payments-model';
import { AdminExtensionSlot, useSlotEntries } from '~/components/workbench/admin-extensions';
import { useResource } from './use-resource';
import { BTN_GHOST, BTN_SECONDARY, CARD, ErrorBanner, LoadError, Pill, Section, SkeletonCard, Spinner } from './ui';

const dateFormat = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' });

function formatDate(iso: string | null): string {
  if (!iso) {
    return '';
  }

  const d = new Date(iso);

  return Number.isNaN(d.getTime()) ? '' : dateFormat.format(d);
}

export function PaymentsList({ appId, defaultMode }: { appId: string; defaultMode: PaymentMode }) {
  const [mode, setMode] = useState<PaymentMode>(defaultMode);
  const payments = useResource(() => apiListPayments(appId, mode), `${appId}:${mode}`);

  const filter = (
    <div className="ui-segmented" role="tablist" aria-label="Payment mode">
      {(['test', 'live'] as const).map((m) => (
        <button
          key={m}
          role="tab"
          aria-selected={mode === m}
          onClick={() => setMode(m)}
          className={classNames('ui-segmented-btn !px-3 !py-1 !text-[11px]', mode === m && 'ui-segmented-btn-active')}
        >
          {m === 'test' ? 'Test' : 'Live'}
        </button>
      ))}
    </div>
  );

  return (
    <Section title="Payments" icon="i-ph:receipt" aside={filter}>
      {payments.loading && !payments.data ? (
        <SkeletonCard lines={3} />
      ) : payments.error ? (
        <LoadError message="Could not load payments." onRetry={() => void payments.reload()} />
      ) : !payments.data?.length ? (
        <div className={classNames(CARD, 'px-4 py-6 text-center')}>
          <div className="i-ph:receipt mx-auto mb-2 text-xl text-faint" />
          <p className="text-[13px] text-primary">No {mode} payments yet</p>
          <p className="mx-auto mt-1 max-w-[260px] text-[12px] leading-relaxed text-muted">
            {mode === 'test'
              ? 'Try a checkout in the preview — test payments show up here. No real money moves.'
              : 'Real payments from your published site appear here once you go live.'}
          </p>
        </div>
      ) : (
        <div className={classNames(CARD, 'divide-y divide-hairline overflow-hidden')}>
          {payments.data.map((row) => (
            <PaymentRow key={row.id} appId={appId} row={row} onChanged={() => void payments.reload()} />
          ))}
        </div>
      )}
    </Section>
  );
}

function PaymentRow({ appId, row, onChanged }: { appId: string; row: PaymentListRow; onChanged: () => void }) {
  const [refunding, setRefunding] = useState(false);
  const [refundInput, setRefundInput] = useState('');
  const [busy, setBusy] = useState<'refund' | 'confirm' | 'reject' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const status = paymentStatusMeta(row.status);
  const date = formatDate(paymentDate(row));
  const remaining = refundableAmount(row);
  // Admin extension blocks for this payment, behind a row expand (there is no payment detail view).
  const hasExtensions = useSlotEntries(appId, 'payments.payment.details').length > 0;
  const [expanded, setExpanded] = useState(false);

  const openRefund = () => {
    setRefundInput(minorToMajorInput(remaining, row.currency));
    setError(null);
    setRefunding(true);
  };

  const refund = async (e: React.FormEvent) => {
    e.preventDefault();

    const amount = majorToMinor(refundInput, row.currency);

    if (amount === null || amount > remaining) {
      setError(`Enter an amount up to ${formatMinor(remaining, row.currency)}.`);
      return;
    }

    setBusy('refund');
    setError(null);

    try {
      // Omit the amount for a full refund so the server refunds exactly what's left.
      await apiRefundPayment(appId, row.id, amount === remaining ? undefined : amount);
      setRefunding(false);
      onChanged();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not refund this payment.'));
    } finally {
      setBusy(null);
    }
  };

  const confirm = async () => {
    setBusy('confirm');
    setError(null);

    try {
      await apiConfirmPayment(appId, row.id);
      onChanged();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not mark this payment as paid.'));
    } finally {
      setBusy(null);
    }
  };

  const reject = async () => {
    const what = row.provider === 'upi' ? 'No UPI payment arrived for this order?' : 'The cash was never collected?';

    if (!window.confirm(`${what} This cancels the order and releases its stock.`)) {
      return;
    }

    setBusy('reject');
    setError(null);

    try {
      await apiRejectPayment(appId, row.id);
      onChanged();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not reject this payment.'));
    } finally {
      setBusy(null);
    }
  };

  const upiCheck = awaitingUpiCheck(row);
  const upiUnpaid = row.provider === 'upi' && row.status === 'pending' && !row.buyerReference && !row.hasScreenshot;

  const viewScreenshot = async () => {
    // Open the tab first (inside the click) so popup blockers allow it, then fill it.
    const tab = window.open('', '_blank');
    setError(null);

    try {
      const blob = await apiGetPaymentProof(appId, row.id);
      const url = URL.createObjectURL(blob);

      if (tab) {
        tab.location.href = url;
      } else {
        window.location.assign(url);
      }

      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      tab?.close();
      setError(apiErrorMessage(err, 'Could not open the screenshot.'));
    }
  };

  return (
    <div className="px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-geist-mono text-[13px] text-primary">{formatMinor(row.amount, row.currency)}</span>
            <Pill tone={status.tone} label={status.label} />
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-1.5 text-[11px] text-muted">
            <span className="text-secondary">{purposeLabel(row.purpose)}</span>
            <span aria-hidden>·</span>
            <span>{providerLabel(row.provider)}</span>
            {row.buyerEmail && (
              <>
                <span aria-hidden>·</span>
                <span className="truncate">{row.buyerEmail}</span>
              </>
            )}
          </div>
          {row.buyerReference && (
            <div className="mt-1 flex items-center gap-1.5 text-[11px]">
              <span className={upiCheck ? 'text-warning' : 'text-muted'}>UTR</span>
              <span className="select-all font-geist-mono text-primary">{row.buyerReference}</span>
              {upiCheck && <span className="text-muted">· check your UPI app for this payment</span>}
            </div>
          )}
          {row.hasScreenshot && (
            <div className="mt-1 flex items-center gap-1.5 text-[11px]">
              <span className={upiCheck ? 'text-warning' : 'text-muted'}>Screenshot</span>
              <button type="button" onClick={viewScreenshot} className="text-primary underline underline-offset-2">
                View
              </button>
              {upiCheck && !row.buyerReference && (
                <span className="text-muted">· screenshots can be edited, check your UPI app</span>
              )}
            </div>
          )}
          {upiUnpaid && (
            <div className="mt-1 text-[11px] text-muted">Waiting for the buyer to pay and share proof.</div>
          )}
          {(date || row.refundedAmount > 0) && (
            <div className="mt-0.5 font-geist-mono text-[10px] text-faint">
              {date}
              {row.refundedAmount > 0 && ` · refunded ${formatMinor(row.refundedAmount, row.currency)}`}
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {canMarkPaid(row) && (
            <button onClick={confirm} disabled={busy !== null} className={BTN_SECONDARY}>
              {busy === 'confirm' && <Spinner />}
              Mark as paid
            </button>
          )}
          {canReject(row) && (
            <button onClick={reject} disabled={busy !== null} className={BTN_GHOST}>
              {busy === 'reject' && <Spinner />}
              Not received
            </button>
          )}
          {canRefund(row) && !refunding && (
            <button onClick={openRefund} disabled={busy !== null} className={BTN_GHOST}>
              Refund
            </button>
          )}
          {hasExtensions && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className={BTN_GHOST}
              aria-expanded={expanded}
              aria-label={expanded ? 'Hide payment details' : 'Show payment details'}
            >
              <div className={classNames(expanded ? 'i-ph:caret-up' : 'i-ph:caret-down', 'text-[12px]')} />
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <AdminExtensionSlot className="mt-3" appId={appId} target="payments.payment.details" resourceId={row.id} />
      )}

      {refunding && (
        <form onSubmit={refund} className="mt-2.5 flex flex-wrap items-center gap-2">
          <input
            value={refundInput}
            onChange={(e) => setRefundInput(e.target.value)}
            inputMode="decimal"
            aria-label={`Refund amount in ${row.currency}`}
            className="ui-input-mono !w-28 !py-1.5"
          />
          <span className="font-geist-mono text-[11px] text-muted">{row.currency}</span>
          <button type="submit" disabled={busy !== null} className={BTN_SECONDARY}>
            {busy === 'refund' && <Spinner />}
            Refund
          </button>
          <button type="button" onClick={() => setRefunding(false)} disabled={busy !== null} className={BTN_GHOST}>
            Cancel
          </button>
        </form>
      )}

      {error && (
        <div className="mt-2">
          <ErrorBanner message={error} />
        </div>
      )}
    </div>
  );
}
